import React, { useState, useEffect, useRef } from 'react';
import './PerformanceMonitor.css';

const PerformanceMonitor = ({ enabled = false, onPerformanceData = null }) => {
  const [stats, setStats] = useState({
    fps: 0,
    memory: 0,
    triangles: 0,
    calls: 0,
    isVisible: false
  });
  
  const intervalRef = useRef(null);
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());

  useEffect(() => {
    if (!enabled) return;

    const updateStats = () => {
      const now = performance.now();
      const delta = now - lastTime.current;
      
      if (delta >= 1000) { // Update every second
        const fps = Math.round((frameCount.current * 1000) / delta);
        frameCount.current = 0;
        lastTime.current = now;

        const memory = performance.memory ? {
          used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
          total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
          limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576) // MB
        } : null;

        const newStats = {
          fps,
          memory: memory ? memory.used : 0,
          memoryTotal: memory ? memory.total : 0,
          memoryLimit: memory ? memory.limit : 0,
          timestamp: now
        };

        setStats(prevStats => ({
          ...prevStats,
          ...newStats
        }));

        // Call callback if provided
        if (onPerformanceData) {
          onPerformanceData(newStats);
        }
      }

      frameCount.current++;
      requestAnimationFrame(updateStats);
    };

    if (enabled) {
      updateStats();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, onPerformanceData]);

  const toggleVisibility = () => {
    setStats(prev => ({ ...prev, isVisible: !prev.isVisible }));
  };

  const getPerformanceStatus = (fps) => {
    if (fps >= 50) return 'excellent';
    if (fps >= 30) return 'good';
    if (fps >= 20) return 'fair';
    return 'poor';
  };

  const getMemoryStatus = (used, limit) => {
    if (!limit) return 'unknown';
    const percentage = (used / limit) * 100;
    if (percentage < 50) return 'good';
    if (percentage < 75) return 'fair';
    return 'high';
  };

  if (!enabled) return null;

  return (
    <div className="performance-monitor">
      <button 
        className="performance-toggle"
        onClick={toggleVisibility}
        title="Toggle Performance Monitor"
      >
        📊
      </button>

      {stats.isVisible && (
        <div className="performance-panel">
          <div className="performance-header">
            <h4>Performance Monitor</h4>
            <button 
              className="performance-close"
              onClick={toggleVisibility}
            >
              ×
            </button>
          </div>

          <div className="performance-stats">
            <div className={`stat-item fps ${getPerformanceStatus(stats.fps)}`}>
              <span className="stat-label">FPS</span>
              <span className="stat-value">{stats.fps}</span>
              <div className="stat-bar">
                <div 
                  className="stat-fill"
                  style={{ width: `${Math.min(100, (stats.fps / 60) * 100)}%` }}
                />
              </div>
            </div>

            {stats.memory > 0 && (
              <div className={`stat-item memory ${getMemoryStatus(stats.memory, stats.memoryLimit)}`}>
                <span className="stat-label">Memory</span>
                <span className="stat-value">{stats.memory}MB</span>
                <div className="stat-bar">
                  <div 
                    className="stat-fill"
                    style={{ 
                      width: stats.memoryLimit 
                        ? `${(stats.memory / stats.memoryLimit) * 100}%` 
                        : '0%' 
                    }}
                  />
                </div>
              </div>
            )}

            <div className="performance-tips">
              {stats.fps < 30 && (
                <div className="tip warning">
                  ⚠️ Low FPS detected. Try reducing graphics quality.
                </div>
              )}
              
              {stats.memory > 100 && (
                <div className="tip warning">
                  ⚠️ High memory usage. Consider closing other tabs.
                </div>
              )}

              {stats.fps >= 50 && stats.memory < 50 && (
                <div className="tip success">
                  ✅ Performance is excellent!
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitor;